import React, { useState } from 'react';
import { X, Calendar, Phone, Mail, MessageSquare, User, FileText, ArrowLeft, Save } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { addCompanyContact } from '../../services/moderatorService';
import { useAuthStore } from '../../stores/authStore';
import { JobApplication } from '../../types';

interface AddContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  application: JobApplication | null;
  onContactAdded: () => void;
}

const AddContactModal: React.FC<AddContactModalProps> = ({
  isOpen,
  onClose,
  application,
  onContactAdded
}) => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    contactDate: new Date().toISOString().split('T')[0],
    contactMethod: 'email' as 'email' | 'phone' | 'linkedin' | 'direct' | 'other',
    contactPerson: '',
    response: 'pending' as 'positive' | 'negative' | 'pending' | 'no-response',
    notes: '',
    followUpDate: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!application || !user) return;

    try {
      setLoading(true);
      await addCompanyContact({
        applicationId: application.id,
        userId: application.userId,
        companyName: application.companyName,
        contactDate: Timestamp.fromDate(new Date(formData.contactDate)),
        contactMethod: formData.contactMethod,
        contactPerson: formData.contactPerson || undefined,
        response: formData.response,
        notes: formData.notes,
        followUpDate: formData.followUpDate ? Timestamp.fromDate(new Date(formData.followUpDate)) : undefined,
        moderatorId: user.uid
      });

      // Feedback di successo
      console.log('✅ Contatto salvato con successo!');
      
      onContactAdded();
      onClose();
      resetForm();
    } catch (error) {
      console.error('❌ Error adding contact:', error);
      alert('Errore nel salvare il contatto. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      contactDate: new Date().toISOString().split('T')[0],
      contactMethod: 'email',
      contactPerson: '',
      response: 'pending',
      notes: '',
      followUpDate: ''
    });
  };

  if (!isOpen || !application) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-blue-50 to-indigo-100 z-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Aggiungi Contatto</h1>
                <p className="text-sm text-gray-600">Registra nuovo contatto azienda</p>
              </div>
            </div>
            
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-xl transition-colors disabled:opacity-50"
              title="Salva contatto"
            >
              <Save className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 overflow-y-auto h-full pb-32">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Informazioni Azienda */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <h3 className="font-bold text-blue-900 text-lg mb-2">Azienda</h3>
              <p className="text-blue-700 font-medium">{application.companyName}</p>
              <p className="text-blue-600 text-sm">{application.position}</p>
            </div>
          </div>

          {/* Data Contatto */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <label className="block text-sm font-bold text-gray-900 mb-3">
              Data Contatto *
            </label>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="date"
                required
                value={formData.contactDate}
                onChange={(e) => setFormData({ ...formData, contactDate: e.target.value })}
                className="pl-12 w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-all"
              />
            </div>
          </div>

          {/* Metodo Contatto */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <label className="block text-sm font-bold text-gray-900 mb-3">
              Metodo Contatto *
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'email', icon: Mail, label: 'Email' },
                { value: 'phone', icon: Phone, label: 'Telefono' },
                { value: 'linkedin', icon: MessageSquare, label: 'LinkedIn' },
                { value: 'letter', icon: FileText, label: 'Lettera' },
                { value: 'direct', icon: User, label: 'Diretto' }
              ].map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, contactMethod: method.value as any })}
                  className={`flex items-center justify-center p-4 border-2 rounded-xl transition-all ${
                    formData.contactMethod === method.value
                      ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <method.icon className="w-5 h-5 mr-2" />
                  <span className="font-medium">{method.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Persona Contattata */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <label className="block text-sm font-bold text-gray-900 mb-3">
              Persona Contattata
            </label>
            <input
              type="text"
              value={formData.contactPerson}
              onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
              placeholder="Nome e cognome della persona contattata"
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-all"
            />
          </div>

          {/* Risposta */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <label className="block text-sm font-bold text-gray-900 mb-3">
              Risposta *
            </label>
            <select
              required
              value={formData.response}
              onChange={(e) => setFormData({ ...formData, response: e.target.value as any })}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-all"
            >
              <option value="pending">In Attesa</option>
              <option value="positive">Positiva</option>
              <option value="negative">Negativa</option>
              <option value="no-response">Nessuna Risposta</option>
            </select>
          </div>

          {/* Data Follow-up */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <label className="block text-sm font-bold text-gray-900 mb-3">
              Data Follow-up <span className="text-gray-500 font-normal">(opzionale)</span>
            </label>
            <input
              type="date"
              value={formData.followUpDate}
              onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-all"
            />
          </div>

          {/* Note */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <label className="block text-sm font-bold text-gray-900 mb-3">
              Note *
            </label>
            <textarea
              required
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Dettagli del contatto, risposta ricevuta, prossimi passi..."
              rows={6}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-all resize-none"
            />
          </div>

          {/* Azioni */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-8">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Salvando...' : 'Salva Contatto'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContactModal; 